import { ActionNode } from '../types';
import { ActionHandler } from '../../../documents/types';

interface CreateCptActionHandlersArgs {
    handleCopyNote?: () => Promise<void>;
    handlePreview?: () => void;
    handleSave?: () => Promise<void>;
    handleComplete?: (postAction?: ActionNode['postAction']) => Promise<void>;
}

const buildBenefitSuggestion = (clinicalIndication?: string): string => {
    const indication = (clinicalIndication || '').toLowerCase();

    if (
        indication.includes('tumor') ||
        indication.includes('mass') ||
        indication.includes('lesion') ||
        indication.includes('cancer')
    ) {
        return 'MRI provides superior soft tissue contrast for lesion characterization, staging, and treatment planning. This information is necessary to establish diagnosis and guide management decisions.';
    }

    if (
        indication.includes('stroke') ||
        indication.includes('acute') ||
        indication.includes('neurologic')
    ) {
        return 'MRI provides high-value neurologic detail for acute diagnostic decision-making, including early ischemic change detection and complication assessment, and can directly influence time-sensitive care.';
    }

    if (
        indication.includes('spine') ||
        indication.includes('cord') ||
        indication.includes('radiculopathy')
    ) {
        return 'MRI provides detailed assessment of neural structures, disc pathology, and canal compromise needed to distinguish etiologies and guide conservative versus interventional care.';
    }

    if (
        indication.includes('vascular') ||
        indication.includes('aneurysm') ||
        indication.includes('stenosis')
    ) {
        return 'MRI/MRA provides high-resolution non-invasive vascular characterization relevant to risk stratification and treatment planning while avoiding ionizing radiation.';
    }

    return 'MRI offers diagnostic information that is not adequately obtainable from alternative imaging, and the expected clinical value is substantial for answering the current indication and guiding management.';
};

const buildProceedConclusion = (benefit?: string, risk?: string): string =>
    `After review of the available evidence and patient-specific factors, the expected diagnostic benefit outweighs the identified MRI safety risks. ${
        benefit ? 'The documented benefit supports proceeding with MRI. ' : ''
    }${
        risk
            ? 'The documented risks can be addressed with targeted mitigations and monitoring.'
            : 'Risks should be addressed with targeted mitigations and monitoring.'
    } Proceed with MRI under the specified safety constraints.`;

const buildDeclineConclusion = (benefit?: string, risk?: string): string =>
    `After review of the available evidence and patient-specific factors, the current MRI safety risks outweigh the expected benefit at this time. ${
        risk
            ? 'The documented risks cannot be reduced to an acceptable level with available mitigations. '
            : 'Risks cannot be reduced to an acceptable level with available mitigations. '
    }${
        benefit
            ? 'Alternative imaging or deferred MRI should be considered until risk conditions change.'
            : 'Alternative imaging or deferred MRI is recommended until additional safety information is available.'
    }`;

export const createCptActionHandlers = ({
    handleCopyNote,
    handlePreview,
    handleSave,
    handleComplete,
}: CreateCptActionHandlersArgs): Record<string, ActionHandler> => {
    const handlers: Record<string, ActionHandler> = {
        aiSuggestBenefit: ({ data, updateData, setValue }) => {
            const suggestion = buildBenefitSuggestion(data?.clinicalIndication);
            updateData('benefit', suggestion);
            setValue('benefit', suggestion);
        },
        aiSuggestProceedConclusion: ({ data, updateData, setValue }) => {
            const suggestion = buildProceedConclusion(
                data?.benefit,
                data?.risk
            );
            updateData('conclusion', suggestion);
            setValue('conclusion', suggestion);
        },
        aiSuggestDeclineConclusion: ({ data, updateData, setValue }) => {
            const suggestion = buildDeclineConclusion(
                data?.benefit,
                data?.risk
            );
            updateData('conclusion', suggestion);
            setValue('conclusion', suggestion);
        },
        // Clears every field in the 76018 Device Details section.
        resetDeviceDetails: ({ updateData, setValue }) => {
            const deviceDetailFields = [
                'deviceType',
                'deviceManufacturer',
                'deviceModelName',
                'ifuUrl',
                'ifuAccessedDate',
                'implantConditions',
            ];
            for (const field of deviceDetailFields) {
                updateData(field, '');
                setValue(field, '');
            }
        },
    };

    if (handleCopyNote) {
        handlers.copyNote = async () => handleCopyNote();
    }
    if (handlePreview) {
        handlers.preview = () => handlePreview();
    }
    if (handleSave) {
        handlers.saveDraft = async () => handleSave();
    }
    if (handleComplete) {
        handlers.completeReport = async ({ action }) =>
            handleComplete(action.postAction);
    }

    return handlers;
};
